import { createContext, useContext, useEffect, useMemo, useState } from "react";

const DiscountContext = createContext(undefined);

const DISCOUNT_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/discounts";

export const DiscountProvider = ({ children }) => {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const authToken = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      const parsed = raw ? JSON.parse(raw) : null;
      return (
        parsed?.authToken || parsed?.token || parsed?.data?.authToken || ""
      );
    } catch {
      return "";
    }
  }, []);

  const fetchDiscountCodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(DISCOUNT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      const list = data?.discounts || data || [];
      if (Array.isArray(list)) {
        const normalized = list.map((item) => ({
          ...item,
          id: item.id || item._id || item.discountId,
          discountId: item.discountId || item.id || item._id,
        }));
        setDiscountCodes(normalized);
      }
    } catch (err) {
      console.error("Failed to fetch discounts", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscountCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const addDiscountCode = async (code) => {
    setLoading(true);
    try {
      await fetch(DISCOUNT_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify(code),
      });
      fetchDiscountCodes();
    } catch (err) {
      console.error("Failed to add discount", err);
    } finally {
      setLoading(false);
    }
  };

  const removeDiscountCode = async (id) => {
    setLoading(true);
    try {
      const target = discountCodes.find((c) => c.id === id || c.discountId === id);
      const discountId = target?.discountId || id;
      await fetch(DISCOUNT_URL, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        // send both discountId and id to avoid server-side mismatch
        body: JSON.stringify({ discountId, id: discountId }),
      });
      fetchDiscountCodes();
    } catch (err) {
      console.error("Failed to delete discount", err);
    } finally {
      setLoading(false);
    }
  };

  const updateDiscountCode = async (id, updates) => {
    setLoading(true);
    try {
      const target = discountCodes.find((c) => c.id === id || c.discountId === id);
      const discountId = updates?.discountId || target?.discountId || id;
      await fetch(DISCOUNT_URL, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify({ discountId, ...updates }),
      });
      fetchDiscountCodes();
    } catch (err) {
      console.error("Failed to update discount", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDiscountCode = (id) => {
    const target = discountCodes.find((c) => c.id === id || c.discountId === id);
    const nextActive = target ? !target.active : true;
    updateDiscountCode(target?.discountId || id, { active: nextActive });
  };

  const validateCode = (code, plan) => {
    const found = discountCodes.find(
      (c) =>
        c.code.toUpperCase() === code.toUpperCase() &&
        c.active &&
        c.plans.includes(plan),
    );
    return found || null;
  };

  return (
    <DiscountContext.Provider
      value={{
        discountCodes,
        loading,
        addDiscountCode,
        removeDiscountCode,
        toggleDiscountCode,
        updateDiscountCode,
        validateCode,
      }}
    >
      {children}
    </DiscountContext.Provider>
  );
};

export const useDiscount = () => {
  const context = useContext(DiscountContext);
  if (!context)
    throw new Error("useDiscount must be used within a DiscountProvider");
  return context;
};
